<template>
  <div id="app">
    <div id="app-content">
      <keep-alive v-if="$route.meta.keepAlive">
        <router-view></router-view>
      </keep-alive>
      <router-view v-else></router-view>

      <!-- Global Bouncing Red Envelope -->
      <div
        v-if="redPacketVisible && !redPacketClosed && !isGamePage"
        class="global-red-envelope"
        @click="goToRedPacket"
      >
        <img src="./assets/img/bonus/red_packet.png" class="global-red-envelope__img" />
        <span class="global-floater-close" @click.stop="redPacketClosed = true">
          <van-icon name="cross" size="12" color="#ffffff" />
        </span>
      </div>

      <!-- Global Treasure Box (treasure_box activity) -->
      <div
        v-if="tbVisible && !tbClosed && !isGamePage && !isActivityPage"
        class="global-treasure-box"
        :style="{ bottom: tbBottom + 'px' }"
        @click="openTreasureBox"
      >
        <div class="global-treasure-box__shine"></div>
        <img
          src="./assets/img/activity/activity_icon/task/close.png"
          class="global-treasure-box__img"
        />
        <span class="global-treasure-box__dot"></span>
        <span class="global-floater-close" @click.stop="tbClosed = true">
          <van-icon name="cross" size="12" color="#ffffff" />
        </span>
      </div>

      <!-- Global First Recharge (first_recharge activity) -->
      <div
        v-if="frVisible && !frClosed && !isGamePage"
        class="global-first-recharge"
        :style="{ bottom: frBottom + 'px' }"
        @click="openFirstRecharge"
      >
        <div class="global-first-recharge__shine"></div>
        <img
          src="./assets/img/activity/activity_icon/task/close.png"
          class="global-first-recharge__img"
        />
        <span v-if="frRedDot" class="global-first-recharge__dot"></span>
        <span v-if="frCountdownText" class="global-first-recharge__timer">{{ frCountdownText }}</span>
        <span class="global-floater-close" @click.stop="frClosed = true">
          <van-icon name="cross" size="12" color="#ffffff" />
        </span>
      </div>

      <!-- Global Treasure Box Popup (shared with Activity page) -->
      <treasure-box-popup
        v-model="showTreasureBoxPopup"
        :price="tbPrice"
        :can-purchase="tbCanPurchase"
        :condition-code="tbConditionCode"
        :total-value="tbTotalValue"
        :rewards="tbRewards"
        :countdown-seconds="tbCountdownSeconds"
        @payment-closed="checkTreasureBox"
      />

      <!-- Global First Recharge Popup -->
      <first-recharge-popup
        v-model="showFirstRechargePopup"
        :pack-data="frPackData"
        @refresh="checkFirstRecharge"
      />
    </div>
  </div>
</template>
<script>
import { RedPacket, GoogleAuthCodeLogin, Init, TreasureBoxInit, NewPlayerGiftPackInit } from '@/api/common'
import TreasureBoxPopup from '@/components/TreasureBoxPopup.vue'
import FirstRechargePopup from '@/components/FirstRechargePopup.vue'
import { contentOf, isOkResponse } from '@/components/loginFlow'
import { safeGetStorageItem, safeLocalStorage, safeRemoveStorageItem, safeSessionStorage, safeSetStorageItem } from '@/utils/safeStorage'

function browserLocation() {
  try {
    if (typeof window !== 'undefined' && window && window.location) {
      return window.location
    }
  } catch (e) {
    return null
  }
  return null
}

function browserHistory() {
  try {
    if (typeof window !== 'undefined' && window && window.history) {
      return window.history
    }
  } catch (e) {
    return null
  }
  return null
}

function browserDocumentTitle() {
  try {
    return typeof document !== 'undefined' && document ? document.title : ''
  } catch (e) {
    return ''
  }
}

function browserLocationValue(key) {
  const location = browserLocation()
  if (!location) return ''
  try {
    return location[key] || ''
  } catch (e) {
    return ''
  }
}

function replaceBrowserState(state, title, url) {
  try {
    const history = browserHistory()
    if (!history || typeof history.replaceState !== 'function') return false
    history.replaceState(state, title, url)
    return true
  } catch (e) {
    return false
  }
}

function cleanGoogleCallbackQueryParams(params) {
  const locationHref = browserLocationValue('href')
  if (!locationHref) return false
  try {
    const url = new URL(locationHref)
    let changed = false
    params.forEach((param) => {
      if (url.searchParams.has(param)) {
        url.searchParams.delete(param)
        changed = true
      }
    })
    if (!changed) return false
    return replaceBrowserState({}, browserDocumentTitle(), url.toString())
  } catch (e) {
    return false
  }
}

function redirectBrowserTo(path) {
  try {
    const location = browserLocation()
    if (!location) return false
    location.href = path
    return true
  } catch (e) {
    return false
  }
}

function reloadLocationSafely() {
  try {
    const location = browserLocation()
    if (!location || typeof location.reload !== 'function') return false
    location.reload()
    return true
  } catch (e) {
    return false
  }
}

function safeDecodeGoogleAuthCode(code) {
  try {
    return decodeURIComponent(String(code == null ? '' : code))
  } catch (e) {
    return String(code == null ? '' : code)
  }
}

const googleCallbackCodesInFlight = new Set()

export default {
  name: 'App',
  components: { TreasureBoxPopup, FirstRechargePopup },
  data() {
    return {
      redPacketVisible: false,
      redPacketClosed: false,
      // Treasure box (treasure_box activity) state
      tbVisible: false,
      tbClosed: false,
      frClosed: false,
      tbPrice: 0,
      tbCanPurchase: false,
      tbConditionCode: '',
      tbTotalValue: 0,
      tbRewards: [],
      tbCountdownSeconds: 86399,
      tbTickTimer: null,
      showTreasureBoxPopup: false,
      // First recharge activity state (新手大礼包)
      frVisible: false,
      frRedDot: false,
      frPackData: null,
      showFirstRechargePopup: false,
      frAutoShown: false,
      frNowTs: Math.floor(Date.now() / 1000),
      frTickTimer: null
    }
  },
  computed: {
    isGamePage() {
      return this.$route.path.includes('gameHall')
    },
    isActivityPage() {
      return this.$route.name === 'Activity'
    },
    tbCountdownText() {
      const total = this.tbCountdownSeconds > 0 ? this.tbCountdownSeconds : 0
      const h = Math.floor(total / 3600)
      const m = Math.floor((total % 3600) / 60)
      const s = total % 60
      return (
        String(h).padStart(2, '0') + ':' +
        String(m).padStart(2, '0') + ':' +
        String(s).padStart(2, '0')
      )
    },
    frCountdownText() {
      if (!this.frPackData) return ''
      const toSec = (t) => {
        const n = Number(t) || 0
        return n > 1e12 ? Math.floor(n / 1000) : n
      }
      // 已购买后不显示任何倒计时
      if (Number(this.frPackData.purchased) === 1) return ''
      // 未购买：到 expireTime 的倒计时
      const target = toSec(this.frPackData.expireTime)
      if (!target) return ''
      const diff = target - this.frNowTs
      if (diff <= 0) return ''
      const d = Math.floor(diff / 86400)
      const h = Math.floor((diff % 86400) / 3600)
      const m = Math.floor((diff % 3600) / 60)
      const s = diff % 60
      if (d > 0) return d + 'd ' + String(h).padStart(2, '0') + ':' + String(m).padStart(2, '0')
      return (
        String(h).padStart(2, '0') + ':' +
        String(m).padStart(2, '0') + ':' +
        String(s).padStart(2, '0')
      )
    },
    // Stacked floating-icon positions. Each slot ~80px tall (icon + countdown pill).
    // Slot 0 (bottom): red envelope → 100
    // Slot 1: treasure box
    // Slot 2: first recharge
    tbBottom() {
      return (this.redPacketVisible && !this.isGamePage) ? 180 : 108
    },
    frBottom() {
      let base = 108
      if (this.redPacketVisible && !this.isGamePage) base += 80
      if (this.tbVisible && !this.tbClosed && !this.isGamePage && !this.isActivityPage) base += 80
      return base
    }
  },
  mounted() {
    this.checkRedPacket()
    this.checkTreasureBox()
    this.checkFirstRecharge()
    this.checkGoogleCallback()
  },
  beforeDestroy() {
    this.stopTbTick()
    this.stopFrTick()
  },
  watch: {
    // 切换路由到首页时重新拉宝箱状态
    $route(to, from) {
      if (to && to.query && to.query.code) {
        this.checkGoogleCallback()
        return
      }
      if (to.path !== from.path && to.path.includes('/home')) {
        this.checkTreasureBox()
      }
    },
    // 宝箱购买弹窗关闭时（用户可能完成/取消付款）重新拉宝箱状态
    showTreasureBoxPopup(val, oldVal) {
      if (oldVal && !val) {
        this.checkTreasureBox()
      }
    }
  },
  methods: {
    postGoogleLoginTarget(prefix) {
      const storage = safeSessionStorage()
      const redirect = safeGetStorageItem(storage, 'otgame_postLoginRedirect')
      safeRemoveStorageItem(storage, 'otgame_postLoginRedirect')
      if (!redirect) return '/' + prefix + '/home'
      const locationOrigin = browserLocationValue('origin')
      if (!locationOrigin) return '/' + prefix + '/home'
      try {
        const url = new URL(redirect, locationOrigin)
        if (url.origin !== locationOrigin) return '/' + prefix + '/home'
        return `${url.pathname}${url.search}${url.hash}`
      } catch (e) {
        return '/' + prefix + '/home'
      }
    },
    okContent(res) {
      if (!isOkResponse(res)) return null
      const content = contentOf(res)
      return Object.keys(content).length ? content : null
    },
    isPositiveFlag(value) {
      if (value === true || value === 1) return true
      const normalized = String(value).trim().toLowerCase()
      return ['1', 'true', 'yes', 'open', 'enabled'].includes(normalized)
    },
    treasureBoxConditionCode(content) {
      const source = content || {}
      const list = source.boxList || source.list || source.rewards || []
      const first = Array.isArray(list) && list.length > 0 && list[0] ? list[0] : {}
      return String(source.conditionCode || source.currentConditionCode || source.recordNo ||
        first.conditionCode || first.condition_code || '').trim()
    },
    isRedPacketVisible(content) {
      if (!content) return false
      if (content.visible !== undefined) return this.isPositiveFlag(content.visible)
      if (content.redPacketOpen !== undefined) return this.isPositiveFlag(content.redPacketOpen)
      if (content.dataUnavailable === true ||
        content.data_unavailable === true ||
        content.technicalMarker === true ||
        content.technical_marker === true ||
        content.backendFailure === true ||
        content.backend_failure === true) return false
      const list = content.list || content.records || content.recordList
      return Array.isArray(list) && list.length > 0
    },
    async checkRedPacket() {
      if (!safeGetStorageItem(safeLocalStorage(), 'token')) return
      try {
        const res = await RedPacket({})
        const c = this.okContent(res)
        this.redPacketVisible = this.isRedPacketVisible(c)
        if (!this.redPacketVisible) this.redPacketClosed = false
      } catch (e) {
        // silent
      }
    },
    async checkTreasureBox() {
      // Only show the treasure box float to logged-in users (mirrors red envelope behavior)
      if (!safeGetStorageItem(safeLocalStorage(), 'token')) {
        this.resetTreasureBoxState()
        return
      }
      try {
        const res = await TreasureBoxInit({})
        const c = this.okContent(res)
        if (c) {
          this.tbCanPurchase = this.isPositiveFlag(c.canPurchase)
          this.tbConditionCode = this.treasureBoxConditionCode(c)
          const visible = c.visible !== 0 && Number(c.purchased) !== 1 && this.tbCanPurchase
          this.tbVisible = visible
          // 重新拉到后端有效状态时，清掉本地关闭标记，让浮标重新可见
          if (visible) this.tbClosed = false
          if (c.price !== undefined) this.tbPrice = c.price
          if (c.totalValue !== undefined) this.tbTotalValue = c.totalValue
          else if (c.price !== undefined) this.tbTotalValue = c.price
          this.tbRewards = Array.isArray(c.rewards) ? c.rewards : []
          if (c.countdownSeconds !== undefined) this.tbCountdownSeconds = c.countdownSeconds
          if (this.tbVisible) this.startTbTick()
          if (!visible) this.resetTreasureBoxState()
        } else {
          this.resetTreasureBoxState()
        }
      } catch (e) {
        this.resetTreasureBoxState()
      }
    },
    resetTreasureBoxState() {
      this.tbVisible = false
      this.tbClosed = false
      this.tbCanPurchase = false
      this.tbConditionCode = ''
      this.tbPrice = 0
      this.tbTotalValue = 0
      this.tbRewards = []
      this.tbCountdownSeconds = 86399
      this.stopTbTick()
    },
    startTbTick() {
      this.stopTbTick()
      this.tbTickTimer = setInterval(() => {
        if (this.tbCountdownSeconds > 0) {
          this.tbCountdownSeconds--
        } else {
          this.stopTbTick()
        }
      }, 1000)
    },
    stopTbTick() {
      if (this.tbTickTimer) {
        clearInterval(this.tbTickTimer)
        this.tbTickTimer = null
      }
    },
    openTreasureBox() {
      this.showTreasureBoxPopup = true
    },
    async checkFirstRecharge() {
      if (!safeGetStorageItem(safeLocalStorage(), 'token')) {
        this.resetFirstRechargeState()
        return
      }
      try {
        const res = await NewPlayerGiftPackInit({})
        const c = this.okContent(res)
        if (c) {
          this.frVisible = Number(c.visible) === 1
          this.frRedDot = Number(c.showRedDot) === 1
          this.frPackData = c
          if (this.frVisible) {
            this.startFrTick()
            if (!this.frAutoShown) {
              this.frAutoShown = true
              this.showFirstRechargePopup = true
            }
          } else {
            this.resetFirstRechargeState()
          }
        } else {
          this.resetFirstRechargeState()
        }
      } catch (e) {
        this.resetFirstRechargeState()
        // silent
      }
    },
    resetFirstRechargeState() {
      this.frVisible = false
      this.frRedDot = false
      this.frPackData = null
      this.frAutoShown = false
      this.stopFrTick()
    },
    startFrTick() {
      this.stopFrTick()
      this.frNowTs = Math.floor(Date.now() / 1000)
      this.frTickTimer = setInterval(() => {
        this.frNowTs = Math.floor(Date.now() / 1000)
      }, 1000)
    },
    stopFrTick() {
      if (this.frTickTimer) {
        clearInterval(this.frTickTimer)
        this.frTickTimer = null
      }
    },
    openFirstRecharge() {
      this.showFirstRechargePopup = true
    },
    goToRedPacket() {
      this.$jumpTo('/redPacket')
    },
    checkGoogleCallback() {
      // Check URL first, then localStorage (saved by router guard before redirects)
      const storage = safeLocalStorage()
      const callbackParams = ['code', 'state', 'scope', 'authuser', 'prompt', 'iss', 'googleRedirectUri', 'redirectUri', 'redirect_uri']
      const locationSearch = browserLocationValue('search')
      const urlParams = new URLSearchParams(locationSearch)
      let code = urlParams.get('code')
      if (code) {
        const callbackRedirectUri = urlParams.get('googleRedirectUri') || urlParams.get('redirectUri') || urlParams.get('redirect_uri') || safeGetStorageItem(storage, 'googleRedirectUri')
        // Clean URL params
        cleanGoogleCallbackQueryParams(callbackParams)
        safeRemoveStorageItem(storage, 'googleAuthCode')
        this.handleGoogleCallback(code, callbackRedirectUri)
      } else {
        cleanGoogleCallbackQueryParams(callbackParams.filter((param) => param !== 'code'))
        code = safeGetStorageItem(storage, 'googleAuthCode')
        if (code) {
          const callbackRedirectUri = safeGetStorageItem(storage, 'googleRedirectUri')
          safeRemoveStorageItem(storage, 'googleAuthCode')
          this.handleGoogleCallback(code, callbackRedirectUri)
        }
      }
    },
    async handleGoogleCallback(code, callbackRedirectUri) {
      const storage = safeLocalStorage()
      const locationOrigin = browserLocationValue('origin')
      const locationPathname = browserLocationValue('pathname')
      const query = (this.$route && this.$route.query) || {}
      const queryRedirectUri = query.googleRedirectUri || query.redirectUri || query.redirect_uri
      const toUrl = callbackRedirectUri || queryRedirectUri || safeGetStorageItem(storage, 'googleRedirectUri') || safeGetStorageItem(storage, 'googleToUrl') || (locationOrigin ? locationOrigin + locationPathname : '')
      const redirectHome = () => {
        const prefix = safeGetStorageItem(storage, 'country') || ''
        safeRemoveStorageItem(storage, 'googleToUrl')
        safeRemoveStorageItem(storage, 'googleRedirectUri')
        redirectBrowserTo('/' + prefix + '/home')
      }
      const normalizedCode = safeDecodeGoogleAuthCode(code)
      if (!normalizedCode) {
        redirectHome()
        return
      }
      if (googleCallbackCodesInFlight.has(normalizedCode)) return
      googleCallbackCodesInFlight.add(normalizedCode)
      try {
        const data = await GoogleAuthCodeLogin({
          code: normalizedCode,
          toUrl: toUrl,
          isWap: true
        })
        const content = this.okContent(data)
        if (!content) {
          redirectHome()
          return
        }

        const prefix = safeGetStorageItem(storage, 'country') || (this.$route && this.$route.params && this.$route.params.prefix) || ''
        if (content.needBindPhone) {
          if (!content.thirdUuid) {
            redirectHome()
            return
          }
          // Need to bind phone — navigate to binding page
          safeSetStorageItem(storage, 'googleThirdUuid', content.thirdUuid)
          safeRemoveStorageItem(storage, 'googleToUrl')
          safeRemoveStorageItem(storage, 'googleRedirectUri')
          redirectBrowserTo('/' + prefix + '/googlePhoneBind')
          return
        }

        if (!content.accessToken) {
          redirectHome()
          return
        }

        // Already registered, login success
        safeSetStorageItem(storage, 'token', content.accessToken)
        safeRemoveStorageItem(storage, 'googleToUrl')
        safeRemoveStorageItem(storage, 'googleRedirectUri')
        redirectBrowserTo(this.postGoogleLoginTarget(prefix))
        setTimeout(() => {
          reloadLocationSafely()
        }, 200)
      } catch (e) {
        redirectHome()
      } finally {
        googleCallbackCodesInFlight.delete(normalizedCode)
      }
    },
    async checkPhoneBind() {
      const storage = safeLocalStorage()
      const prefix = safeGetStorageItem(storage, 'country') || (this.$route && this.$route.params && this.$route.params.prefix) || ''
      try {
        const res = await Init()
        const content = this.okContent(res)
        if (!content) {
          redirectBrowserTo('/' + prefix + '/home')
          return
        }

        if (!content.phoneNo) {
          // No phone bound — force bind
          redirectBrowserTo('/' + prefix + '/phoneUnbound?fromGoogle=1')
        } else {
          // Phone already bound — go to home
          redirectBrowserTo(this.postGoogleLoginTarget(prefix))
          setTimeout(() => {
            reloadLocationSafely()
          }, 200)
        }
      } catch (e) {
        redirectBrowserTo('/' + prefix + '/home')
      }
    }
  }
}
</script>
<style>
html,
body {
  margin: 0;
  padding: 0;
  color: #fff;
  font-size: var(--vk-fs-xs);
}
ul,
p,
h3 {
  margin: 0;
  padding: 0;
}
/* #app-content {
  background-color: #0e131c;
} */
/* 基础样式 */
#app {
  width: 100%;
  transition: all 0.3s ease;
}

/* 移动端样式 */
@media (max-width: 768px) {
  body {
    background-color: #1a1d29;
    min-height: 100vh;
    /* background: url(./assets/img/page_bg2.png) no-repeat; */
    background-size: cover;
    height: 100vh;
    overflow-y: scroll;
  }
  #app {
    /* 移动端全屏显示 */
    width: 100%;
    padding: 0;
    height: 100vh;
    overflow: scroll;
  }
}

/* PC 端样式 */
@media (min-width: 769px) {
  body {
    /* 宽屏 gutter 用品牌深底(原 #edf2fa 浅灰会让深色 app 两侧露白、像没做完)*/
    background-color: var(--vk-bg, #0e1116);
  }

  #app-content {
    /* 设置固定宽度并居中 */
    width: var(--vk-pc-col);
    margin: 0 auto;
    min-height: 100vh;
    background-color: #1a1d29;
    /* background: url(./assets/img/page_bg2.png) no-repeat; */
    background-size: cover;
    height: 100vh;
    overflow-y: scroll;
    /* 居中手机列的设备感:与更深的 gutter 拉出层次 */
    box-shadow: 0 0 32px rgba(0, 0, 0, 0.55);
  }
  .van-nav-bar {
    width: var(--vk-pc-col) !important;
  }
  .van-nav-bar--fixed {
    left: 50% !important;
    transform: translate(-50%, -50%);
    top: 30px !important;
  }
  .van-popup {
    width: calc(var(--vk-pc-col) - 20px) !important;
  }
  .van-sticky--fixed {
    width: var(--vk-pc-col) !important;
    left: 50% !important;
    transform: translate(-50%, -50%);
    top: 72px !important;
  }
  .van-popup--bottom {
    left: 50% !important;
    transform: translate(-50%, -0%);
    width: var(--vk-pc-col) !important;
  }
}
/* ====== Global Red Envelope ====== */
.global-red-envelope {
  position: fixed;
  right: 16px;
  bottom: 100px;
  z-index: 999;
  cursor: pointer;
  animation: global-red-bounce 1.5s ease-in-out infinite;
}
.global-red-envelope__img {
  width: 56px;
  height: 56px;
  object-fit: contain;
  filter: drop-shadow(0 4px 12px rgba(255, 50, 50, 0.4));
}
@keyframes global-red-bounce {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-12px); }
}

/* ====== Global Treasure Box (first deposit gift) ====== */
.global-floater-close {
  position: absolute !important;
  top: -6px !important;
  right: -6px !important;
  left: auto !important;
  bottom: auto !important;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.75);
  border: 1px solid rgba(255, 255, 255, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 3;
  cursor: pointer;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.6);
}
.global-floater-close:active {
  transform: scale(0.9);
}
.global-treasure-box {
  position: fixed;
  right: 16px;
  z-index: 999;
  width: 56px;
  height: 56px;
  cursor: pointer;
  animation: tb-float 1.8s ease-in-out infinite;
  /* bottom is bound inline so we can react to red-envelope visibility */
  transition: bottom 0.3s ease;
}
.global-treasure-box__img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  animation: tb-glow 2s ease-in-out infinite;
}
.global-treasure-box__shine {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  border-radius: 50%;
  pointer-events: none;
  overflow: hidden;
}
.global-treasure-box__shine::before {
  content: '';
  position: absolute;
  top: -50%;
  left: -60%;
  width: 40%;
  height: 200%;
  background: linear-gradient(
    120deg,
    transparent 0%,
    rgba(255, 255, 255, 0) 40%,
    rgba(255, 245, 120, 0.55) 50%,
    rgba(255, 255, 255, 0) 60%,
    transparent 100%
  );
  transform: rotate(25deg);
  animation: tb-shine 3s ease-in-out infinite;
}
.global-treasure-box__dot {
  position: absolute;
  top: -2px;
  right: -2px;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: #ff3b30;
  box-shadow: 0 0 8px rgba(255, 59, 48, 0.8);
  animation: tb-dot-pulse 0.9s ease-in-out infinite;
}
.global-treasure-box__timer {
  position: absolute;
  left: 50%;
  bottom: -10px;
  transform: translateX(-50%);
  padding: 2px 6px;
  border-radius: 8px;
  background: rgba(10, 14, 26, 0.9);
  border: 1px solid rgba(248, 245, 16, 0.45);
  color: #f8f510;
  font-size: var(--vk-fs-2xs);
  font-weight: 900;
  line-height: 1;
  letter-spacing: 0.5px;
  white-space: nowrap;
  text-shadow: 0 0 6px rgba(248, 245, 16, 0.5);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.5);
  pointer-events: none;
}
@keyframes tb-float {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-6px); }
}
@keyframes tb-glow {
  0%, 100% {
    filter: drop-shadow(0 4px 10px rgba(248, 245, 16, 0.35))
            drop-shadow(0 0 6px rgba(248, 245, 16, 0.25));
  }
  50% {
    filter: drop-shadow(0 6px 16px rgba(248, 245, 16, 0.75))
            drop-shadow(0 0 14px rgba(248, 245, 16, 0.55));
  }
}
@keyframes tb-shine {
  0%   { left: -60%; }
  60%  { left: 120%; }
  100% { left: 120%; }
}
@keyframes tb-dot-pulse {
  0%, 100% { transform: scale(1); opacity: 1; }
  50%      { transform: scale(1.35); opacity: 0.8; }
}

/* ====== Global First Recharge Floater ====== */
.global-first-recharge {
  position: fixed;
  right: 16px;
  z-index: 999;
  width: 56px;
  height: 56px;
  cursor: pointer;
  animation: fr-float 2s ease-in-out infinite;
  transition: bottom 0.3s ease;
}
.global-first-recharge__img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  animation: fr-glow 2.2s ease-in-out infinite;
}
.global-first-recharge__dot {
  position: absolute;
  top: -2px;
  right: -2px;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: #ff3b30;
  box-shadow: 0 0 8px rgba(255, 59, 48, 0.8);
  animation: tb-dot-pulse 0.9s ease-in-out infinite;
  z-index: 2;
}
.global-first-recharge__shine {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  border-radius: 50%;
  pointer-events: none;
  overflow: hidden;
}
.global-first-recharge__shine::before {
  content: '';
  position: absolute;
  top: -50%;
  left: -60%;
  width: 40%;
  height: 200%;
  background: linear-gradient(
    120deg,
    transparent 0%,
    rgba(255, 255, 255, 0) 40%,
    rgba(216, 130, 255, 0.6) 50%,
    rgba(255, 255, 255, 0) 60%,
    transparent 100%
  );
  transform: rotate(25deg);
  animation: fr-shine 3.2s ease-in-out infinite;
}
.global-first-recharge__timer {
  position: absolute;
  left: 50%;
  bottom: -10px;
  transform: translateX(-50%);
  padding: 2px 6px;
  border-radius: 8px;
  background: rgba(10, 14, 26, 0.9);
  border: 1px solid rgba(168, 28, 230, 0.5);
  color: #ffcd34;
  font-size: var(--vk-fs-2xs);
  font-weight: 900;
  line-height: 1;
  letter-spacing: 0.5px;
  white-space: nowrap;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.5);
  pointer-events: none;
}
@keyframes fr-float {
  0%, 100% { transform: translateY(0); }
  50%      { transform: translateY(-6px); }
}
@keyframes fr-glow {
  0%, 100% {
    filter: drop-shadow(0 4px 10px rgba(168, 28, 230, 0.35))
            drop-shadow(0 0 6px rgba(168, 28, 230, 0.25));
  }
  50% {
    filter: drop-shadow(0 6px 16px rgba(168, 28, 230, 0.75))
            drop-shadow(0 0 14px rgba(168, 28, 230, 0.55));
  }
}
@keyframes fr-shine {
  0%   { left: -60%; }
  60%  { left: 120%; }
  100% { left: 120%; }
}
</style>
